<template>
  <div
    class="podcast-card position-relative mb-4"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div
      class="mr-3"
      :class="{ 'cursor-pointer': podcast.active }"
      @click="addEpisodes(podcast)"
    >
      <HMFixedAspectRatioContainer aspectRatio="1by1">
        <PodcastThumbnail
          class="pointer"
          :podcast="podcast"
          :hideOverlay="false"
          :active="podcast.active"
        />
      </HMFixedAspectRatioContainer>
    </div>
    <div
      class="d-flex mt-3 flex-grow-1 flex-column mr-3"
      :class="{ archived: !podcast.active }"
    >
      <div class="d-flex justify-content-between">
        <div
          @click="addEpisodes(podcast)"
          :class="{ 'cursor-pointer': podcast.active }"
        >
          <v-clamp
            autoresize
            :max-lines="2"
            class="podcast-title h4 m-0 media-title mb-2"
          >
            {{ podcast.title }}
          </v-clamp>
        </div>
        <div>
          <HMIconButton
            :icon="['far', 'ellipsis-h']"
            :id="`popover-${podcast.id}`"
          />
          <HMPopover :target="`popover-${podcast.id}`" placement="topleft">
            <HMPopoverMenuItem @click="addEpisodes(podcast)">
              {{ $t('PODCASTS.LINK.ADD_NEW_EPISODES') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem @click="editPodcast(podcast)">
              {{ $t('PODCASTS.LINK.EDIT_PODCAST') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem @click="viewPodcastMembers(podcast)">
              {{ $t('PODCASTS.LINK.VIEW_LISTENERS') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem
              v-if="podcast.active"
              @click="unpublishPodcast(podcast)"
            >
              {{ $t('PODCASTS.LINK.UNPUBLISH_PODCAST') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem v-else @click="publishPodcast(podcast)">
              {{ $t('PODCASTS.LINK.PUBLISH_PODCAST') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem @click="showDeleteModal(podcast)">
              <div
                class="d-flex flex-row justify-content-between align-items-center"
              >
                <i class="far fa-trash-alt c-coral"></i>
                <span>{{ $t('PODCASTS.LINK.DELETE_PODCAST') }}</span>
              </div>
            </HMPopoverMenuItem>
          </HMPopover>
        </div>
      </div>
      <div class="d-flex justify-content-between">
        <span v-if="podcast.is_free" class="pointer hover-coral small">
          {{ $t('PODCASTS.TEXT.EVERYONE') }}
        </span>
        <span
          v-else
          class="pointer hover-coral small"
          @click="viewPodcastMembers(podcast)"
        >
          {{
            $tc('PODCASTS.TEXT.LISTENERS_VARIANT', podcast.num_members, {
              count: podcast.num_members,
            })
          }}
        </span>
        <router-link
          class="undecorated-router-link hover-coral small"
          :to="{
            name: 'Podcast episodes',
            params: { id: podcast.id },
          }"
        >
          {{
            $tc('PODCASTS.TEXT.EPISODES_VARIANT', podcast.num_episodes, {
              count: podcast.num_episodes,
            })
          }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import { errorToMessage } from '@/core/utils/errors';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import { cloneDeep } from 'lodash';
import VClamp from 'vue-clamp';
import { mapGetters } from 'vuex';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';
import PodcastThumbnail from './PodcastThumbnail';
import PodcastMembersModal from './PodcastMembersModal';
import HMDialog from '../common/HMDialog';

export default Vue.extend({
  name: 'PodcastCard',
  props: {
    podcast: {
      type: Object,
      required: true,
    },
  },
  components: {
    PodcastThumbnail,
    VClamp,
  },
  computed: mapGetters({
    teacherStudioUrl: 'user/teacherStudioUrl',
    teacherMagicCode: 'user/teacherMagicCode',
  }),
  methods: {
    async editPodcast(podcast) {
      this.$router.push({
        name: 'Edit podcast',
        params: { id: podcast.id },
      });
    },
    async addEpisodes(podcast) {
      this.$router.push({
        name: 'Podcast episodes',
        params: { id: podcast.id },
      });
    },
    viewPodcastMembers(podcast) {
      this.$modal.show(PodcastMembersModal, {
        podcast,
      });
    },
    async unpublishPodcast(podcast) {
      try {
        this.inProcess = true;
        await this.$store.dispatch('podcasts/setPodcastActiveFlag', {
          podcastId: podcast.id,
          active: false,
        });
        podcast.active = false;

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PODCASTS.NOTIFICATIONS.PODCAST_WAS_SUCCESSFULLY_UNPUBLISHED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async publishPodcast(podcast) {
      try {
        this.inProcess = true;
        await this.$store.dispatch('podcasts/setPodcastActiveFlag', {
          podcastId: podcast.id,
          active: true,
        });
        podcast.active = true;

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PODCASTS.NOTIFICATIONS.PODCAST_WAS_SUCCESSFULLY_PUBLISHED'
          ),
        });
      } catch (error) {
        this.$emit('showUpgradeYourPlanModal');
      } finally {
        this.inProcess = false;
      }
    },
    async showDeleteModal(podcast) {
      this.$modal.show(HMDialog, {
        title: this.$t('PODCASTS.TITLES.DELETE_PODCAST'),
        text: this.$t('PODCASTS.TEXT.CONFIRM_DELETE_PODCAST', {
          title: podcast.title,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.DELETE'),
        confirm: async () => {
          await this.deletePodcast(podcast);
        },
      });
    },
    async deletePodcast(podcast) {
      try {
        this.inProcess = true;
        await this.$store.dispatch('podcasts/deletePodcast', podcast.id);

        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PODCASTS.NOTIFICATIONS.SUCCESSFULLY_DELETED'),
        });
        this.$emit('loadPodcasts');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete this podcast',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    previewPodcast(data) {
      if (!data.active) return;
      const route = this.$router.resolve({
        name: 'studio-podcast',
        params: {
          id: data.id,
          studio_url: this.teacherStudioUrl,
        },
      });
      const routeUrl = route.href.replaceAll(`${this.teacherStudioUrl}/`, '');
      const url = getStudentViewURL(
        this.teacherStudioUrl,
        routeUrl,
        this.teacherMagicCode
      );

      window.open(url, '_blank');
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.podcast-card {
  min-width: 256px;

  &:hover {
    .podcast-title {
      color: $coral;
    }
  }
}

.podcast-title {
  max-width: 180px;
  @media screen and (max-width: 768px) {
    max-width: 270px;
  }
}

.archived {
  opacity: 0.5;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
